import PropTypes from 'prop-types';
import React from 'react';
import { FormControl } from 'react-bootstrap';
import _ from 'lodash';
import i18nCountries from 'i18n-iso-countries';
import translate from '../../translate';
import ValidatorFormGroup from '../../main/validator-form-group';
import { DEF_VALIDATOR } from '../../../util/validators';
import { DropdownList } from 'react-widgets';
import memoize from 'memoize-one';
import './countriesConfig';

const REQUIRED = true;

const NOT_REQUIRED = false;

const SUPPLEMENTARY_LINE_1 = 'supplementary_line1';
const SUPPLEMENTARY_LINE_2 = 'supplementary_line2';
const SUPPLEMENTARY_LINE_3 = 'supplementary_line3';

const VALIDATORS = {
    phone: {
        isValid(val) {
            if (!val) {
                return true;
            }
            return !!val && /^\s*\+?\(?\s*(\d[\s\(\)\/-]*){5,}$/.test(val);
        },
    },
    company: {
        isValid(val) {
            return !!val && /^\s*\S+.*$/.test(val);
        },
    },
    [SUPPLEMENTARY_LINE_1]: {
        isValid() {
            return true;
        },
    },
    [SUPPLEMENTARY_LINE_2]: {
        isValid() {
            return true;
        },
    },
    [SUPPLEMENTARY_LINE_3]: {
        isValid() {
            return true;
        },
    },
    name: {
        isValid() {
            return true;
        },
    },
    street_and_housenumber: {
        isValid(val) {
            return !!val && DEF_VALIDATOR.test(val);
        },
    },
    city: {
        isValid(val) {
            return (
                !!val && DEF_VALIDATOR.test(val)
            );
        },
    },
    postal_code: {
        isValid(val) {
            return !!val && DEF_VALIDATOR.test(val);
        },
    },
    country: {
        isValid(val) {
            return !!val && DEF_VALIDATOR.test(val);
        },
    },
    country_iso: {
        isValid(val) {
            return !!val;
        },
    },
};

class ProfileContactDataForm extends React.Component {
    static propTypes = {
        contactData: PropTypes.shape({
            phone: PropTypes.string,
            company: PropTypes.string,
            [SUPPLEMENTARY_LINE_1]: PropTypes.string,
            [SUPPLEMENTARY_LINE_2]: PropTypes.string,
            [SUPPLEMENTARY_LINE_3]: PropTypes.string,
            name: PropTypes.string,
            street_and_housenumber: PropTypes.string,
            city: PropTypes.string,
            postal_code: PropTypes.string,
            country: PropTypes.string,
            country_iso: PropTypes.string,
        }),
        handleChange: PropTypes.func,
        disabled: PropTypes.bool,
        optional: PropTypes.bool,
        error: PropTypes.bool,
        strings: PropTypes.shape({
            chooseToUse: PropTypes.string.isRequired,
            genericError: PropTypes.string.isRequired,
            comboboxHelp: PropTypes.shape({
                openCombobox: PropTypes.string.isRequired,
                emptyList: PropTypes.string.isRequired,
                emptyFilter: PropTypes.string.isRequired,
            }).isRequired,
            _utils: PropTypes.shape({ lang: PropTypes.string }),
        }).isRequired,
    };

    static defaultProps = {
        contactData: undefined,
        optional: false,
        disabled: false,
        error: false,
        handleChange: undefined,
    };

    state = {
        chosenToUse: !!this.props.contactData,
        contactData: { ...this.props.contactData },
        _dirty: toDirtyKeys(this.props.contactData),
        _valid: true,
        dataSet: false,
    };

    componentWillReceiveProps(newProps) {
        if (
            JSON.stringify(this.props.contactData) !==
            JSON.stringify(newProps.contactData)
        ) {
            const chosenToUse = !!newProps.contactData;
            this.setState({
                chosenToUse,
                contactData: newProps.contactData || {},
            });
        }
    }

    getFieldValue = (field) => {
        return this.state.contactData[field];
    };

    _validateField = (data, field) => {
        if (!VALIDATORS[field]) {
            return true;
        }
        return VALIDATORS[field].isValid(data[field]);
    };

    _isDataValid = (data) => {
        if (!data.chosenToUse && this.props.optional) {
            return true;
        }
        return Object.keys(VALIDATORS).reduce((sum, field) => {
            const valid = this._validateField(data.contactData, field);
            return sum && valid;
        }, true);
    };

    _handleFieldChange = (field, value) => {
        const data = _.extend({}, this.state);
        data.contactData = _.extend({}, this.state.contactData);
        data.contactData[field] = value;
        data._dirty[field] = true;
        data._valid = this._isDataValid(data);
        this.setState(data, () => this._handleStateChange());
    };

    _handleCountryChange = (field, fieldIso, valueIso) => {
        const value = i18nCountries.getNames('en')[valueIso] || '';
        const data = _.extend({}, this.state);
        data.contactData = _.extend({}, this.state.contactData);
        data.contactData[field] = value;
        data._dirty[field] = true;
        data.contactData[fieldIso] = valueIso;
        data._dirty[fieldIso] = true;
        data._valid = this._isDataValid(data);
        this.setState(data, () => this._handleStateChange());
    };

    _handleFormToggle = (toggle) => {
        const data = _.extend(
            {
                contactData: _.extend({}, this.state.contactData),
            },
            this.state
        );
        data.chosenToUse = toggle;
        data._valid = this._isDataValid(data);
        this.setState(data, () => this._handleStateChange());
    };

    _handleStateChange = () => {
        if (this.props.handleChange) {
            const chosenToUse = !this.props.optional || this.state.chosenToUse;
            this.props.handleChange({
                _valid: this.state._valid,
                chosenToUse,
                contactData: chosenToUse ? { ...this.state.contactData } : null,
            });
        }
    };

    _shouldHideErrorForField = (field) => {
        return (
            !this.state._dirty[field] ||
            this._validateField(this.state.contactData, field)
        );
    };

    _getIsoCodesAndCountriesMemo = memoize((language) => {
        const realLang = language === 'ger' ? 'de' : 'en';
        const countries = i18nCountries.getNames(realLang);
        const isoCodes = Object.keys(countries);
        isoCodes.sort((a, b) => (countries[a] < countries[b] ? -1 : 1));
        return isoCodes.map((iso) => ({ id: iso, name: countries[iso] }));
    });

    _getIsoCodeAndCountryMemo = memoize((isoCode, isoCodeAndCountries) => {
        return isoCodeAndCountries.find((it) => it.id === isoCode);
    });

    maybeRenderToggleFormCheckbox = () => {
        if (!this.props.optional) {
            return null;
        }
        return (
            <ValidatorFormGroup label="" hideErrorLabel errorLabel="">
                <div className="checkbox checkbox-primary checkbox-custom">
                    <input
                        type="checkbox"
                        id="chosenToUse"
                        className="styled"
                        checked={this.state.chosenToUse}
                        disabled={this.props.disabled}
                        onChange={(event) =>
                            this._handleFormToggle(event.target.checked)
                        }
                    />
                    <label htmlFor="chosenToUse">
                        {this.props.strings.chooseToUse}
                    </label>
                </div>
            </ValidatorFormGroup>
        );
    };

    maybeRenderError = () => {
        if (!this.props.error) {
            return null;
        }

        return (
            <div className="alert alert-danger">
                {this.props.strings.genericError}
            </div>
        );
    };

    renderStringInput = (field, required) => {
        return (
            <FormControl
                type={'text'}
                className={'input-text-color'}
                value={this.getFieldValue(field) || ''}
                onChange={(event) =>
                    this._handleFieldChange(field, event.target.value)
                }
                required={required}
                disabled={this.props.disabled}
            />
        );
    };

    renderStringInputWithErrorLabel = (field, required) => {
        const texts = this.props.strings[field] || { label: field, error: '' };

        return (
            <ValidatorFormGroup
                label={texts.label}
                hideErrorLabel={this._shouldHideErrorForField(field)}
                errorLabel={texts.error}
            >
                {this.renderStringInput(field, required)}
            </ValidatorFormGroup>
        );
    };

    renderCountryInputWithErrorLabel = (field, fieldIso) => {
        const texts = this.props.strings[field] || { label: field, error: '' };
        const isoCodeAndCountry = this._getIsoCodesAndCountriesMemo(
            this.props.strings._utils.lang
        );
        const selectedValue =
            this._getIsoCodeAndCountryMemo(
                this.getFieldValue(fieldIso),
                isoCodeAndCountry
            ) || this.getFieldValue(field);
        const comboTexts = this.props.strings.comboboxHelp;

        return (
            <ValidatorFormGroup
                label={texts.label}
                hideErrorLabel={this._shouldHideErrorForField(fieldIso)}
                errorLabel={texts.error}
            >
                <DropdownList
                    defaultValue={selectedValue}
                    data={isoCodeAndCountry}
                    dataKey={'id'}
                    textField={'name'}
                    filter={'contains'}
                    onChange={(value) =>
                        this._handleCountryChange(field, fieldIso, value.id)
                    }
                    value={selectedValue}
                    disabled={this.props.disabled}
                    messages={comboTexts}
                />
            </ValidatorFormGroup>
        );
    };

    render() {
        const showFullForm = !this.props.optional || this.state.chosenToUse;
        if (showFullForm) {
            return (
                <div>
                    {this.maybeRenderToggleFormCheckbox()}
                    {this.renderStringInputWithErrorLabel('company', REQUIRED)}
                    {this.renderStringInputWithErrorLabel(
                        SUPPLEMENTARY_LINE_1,
                        NOT_REQUIRED
                    )}
                    {this.renderStringInputWithErrorLabel(
                        SUPPLEMENTARY_LINE_2,
                        NOT_REQUIRED
                    )}
                    {this.renderStringInputWithErrorLabel(
                        SUPPLEMENTARY_LINE_3,
                        NOT_REQUIRED
                    )}
                    {this.renderStringInputWithErrorLabel('name', NOT_REQUIRED)}
                    {this.renderStringInputWithErrorLabel(
                        'street_and_housenumber',
                        REQUIRED
                    )}
                    {this.renderStringInputWithErrorLabel(
                        'postal_code',
                        REQUIRED
                    )}
                    {this.renderStringInputWithErrorLabel('city', REQUIRED)}
                    {this.renderCountryInputWithErrorLabel(
                        'country',
                        'country_iso'
                    )}
                    {this.renderStringInputWithErrorLabel(
                        'phone',
                        NOT_REQUIRED
                    )}
                    {this.maybeRenderError()}
                </div>
            );
        } else {
            return (
                <div>
                    {this.maybeRenderToggleFormCheckbox()}
                    {this.maybeRenderError()}
                </div>
            );
        }
    }
}

function toDirtyKeys(valueObj) {
    if (!valueObj) {
        return {};
    }

    const _dirty = {};
    Object.keys(valueObj).forEach((key) => (_dirty[key] = true));
    return _dirty;
}

export default translate('ProfileContactDataForm')(ProfileContactDataForm);
